import { createConsentStore, INITIAL_CONSENT_SNAPSHOT, type ConsentCallback } from "./store";
import { hasNoticeAcknowledgment, noticeAcknowledgmentCookie, US_NOTICE_PENDING_KEY } from "./storage";
import type { IubendaPreferences } from "./model";
import { installClarityController, updateClarityConsent } from "./clarity";
declare global {
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
  interface Window {
    _iub?: {
      cc?: string;
      cs?: {
        api?: {
          getPreferences?: () => IubendaPreferences | undefined;
          openPreferences?: () => void;
        };
      };
    };
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    ousterConsentCallback?: (event: ConsentCallback) => void;
  }
}
let store: ReturnType<typeof createConsentStore> | undefined;
const listeners = new Set<() => void>();
function readNative() {
  let preferences: IubendaPreferences | undefined;
  let readFailed = false;
  try {
    const api = window._iub?.cs?.api;
    if (!api?.getPreferences) readFailed = true;else {
      preferences = api.getPreferences();
      if (!preferences || typeof preferences !== "object") readFailed = true;
    }
  } catch {
    readFailed = true;
  }
  return {
    preferences,
    readFailed,
    countryCode: window._iub?.cc,
    globalPrivacyControl: navigator.globalPrivacyControl
  };
}
export const getConsentSnapshot = () => store?.getSnapshot() ?? INITIAL_CONSENT_SNAPSHOT;
export const getServerConsentSnapshot = () => INITIAL_CONSENT_SNAPSHOT;
export function subscribeToConsent(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
export function initializeConsentController() {
  if (store || typeof window === "undefined") return;
  installClarityController();
  let acknowledged = false;
  let pending = false;
  try {
    acknowledged = hasNoticeAcknowledgment(document.cookie);
  } catch {
    /* Memory-only when storage is blocked. */
  }
  try {
    pending = sessionStorage.getItem(US_NOTICE_PENDING_KEY) === "1";
  } catch {
    /* Memory-only. */
  }
  store = createConsentStore({
    read: readNative,
    acknowledged,
    pending,
    persistAcknowledgment() {
      try {
        document.cookie = noticeAcknowledgmentCookie(window.location.protocol === "https:");
      } catch {
        /* Memory-only. */
      }
    },
    persistPending(value) {
      try {
        if (value) sessionStorage.setItem(US_NOTICE_PENDING_KEY, "1");else sessionStorage.removeItem(US_NOTICE_PENDING_KEY);
      } catch {
        /* Memory-only. */
      }
    },
    updateGoogle(state) {
      // Close Clarity's start/transport/storage gates before SDK Google listeners see a loss.
      updateClarityConsent(state);
      // Reuse the canonical head function, which pushes an actual Arguments object.
      window.gtag?.("consent", "update", state);
    },
    observe(snapshot) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "ouster_consent_snapshot",
        ouster_consent: snapshot
      });
    }
  });
  store.subscribe(() => listeners.forEach(listener => listener()));
  window.ousterConsentCallback = event => store?.receive(event);
  window.addEventListener("pageshow", () => store?.refresh());
  window.addEventListener("focus", () => store?.refresh());
}
export function exposeConsentNotice() {
  store?.exposeNotice();
}
export function acknowledgeConsentNotice(reason: "close" | "browsing") {
  store?.acknowledge(reason);
}
export function openConsentPreferences() {
  const api = window._iub?.cs?.api;
  if (!getConsentSnapshot().ready || !api?.openPreferences) return;
  store?.beginPreferences();
  try {
    api.openPreferences();
  } catch {
    store?.receive("closed");
  }
}
let reloadRequested = false;
/** Loaded optional SDKs cannot be stopped by unmounting a next/script element. */
export function requestConsentReload() {
  if (reloadRequested || typeof window === "undefined") return;
  reloadRequested = true;
  window.location.reload();
}